import { cn } from "@/lib/utils";
import {
  isConversionTargetSupported,
  ModelBackendEnum,
  ModelOutputTypeEnum,
  ModelQuantizationEnum,
  ModelRegionEnum,
  ProjectTypeEnum,
} from "@repo/schema";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FREEZE_MODES,
  FREEZE_MODE_LABELS,
  FreezeMode,
  MODEL_SIZE_HINTS,
  MODEL_SIZE_LABELS,
  ModelSize,
  SELECTABLE_MODEL_SIZES,
  parseHyperparamsObject,
  patchHyperparam,
  readFreezeMode,
  readModelSize,
  toNativeSize,
  writeFreezeMode,
} from "../../utils/hyperparams";
import { SettingsCard } from "../SettingsCard";
import { HyperparamsModal } from "./HyperparamsModal";

const REGION_LABELS: Record<ModelRegionEnum, string> = {
  [ModelRegionEnum.EUROPE_WEST4]: "europe-west4",
  [ModelRegionEnum.US_CENTRAL1]: "us-central1",
};

export interface AdvancedSettingsProps {
  backend: ModelBackendEnum;
  trainingType: ProjectTypeEnum;
  outputs: ModelOutputTypeEnum[];
  onOutputsChange: (outputs: ModelOutputTypeEnum[]) => void;
  quantization: ModelQuantizationEnum;
  onQuantizationChange: (quantization: ModelQuantizationEnum) => void;
  region: ModelRegionEnum;
  onRegionChange: (region: ModelRegionEnum) => void;
  customHyperparams: string;
  onCustomHyperparamsChange: (value: string) => void;
  hyperparamsError: string | null;
  onHyperparamsErrorChange: (error: string | null) => void;
}

export const AdvancedSettings = ({
  backend,
  trainingType,
  outputs,
  onOutputsChange,
  quantization,
  onQuantizationChange,
  region,
  onRegionChange,
  customHyperparams,
  onCustomHyperparamsChange,
  hyperparamsError,
  onHyperparamsErrorChange,
}: AdvancedSettingsProps) => {
  const { t } = useTranslation(["model", "common"]);
  const [modalOpen, setModalOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const handleApply = (value: string) => {
    onCustomHyperparamsChange(value);
    onHyperparamsErrorChange(null);
    setModalOpen(false);
  };

  const hasSummary = customHyperparams.trim().length > 0;

  // Model size and layer freezing are two keys inside the hyperparams
  // blob. Deriving them rather than holding state keeps them in step with
  // presets, the AI suggestion and hand edits in the JSON editor; a blob that
  // doesn't parse disables both controls.
  const parsedHyperparams = parseHyperparamsObject(customHyperparams);
  const paramsEditable = parsedHyperparams !== null;
  const modelSize = readModelSize(trainingType, parsedHyperparams);
  const freezeMode = readFreezeMode(parsedHyperparams);

  const handleModelSizeChange = (size: ModelSize) => {
    const native = toNativeSize(trainingType, size);
    if (native === null) return;
    const next = patchHyperparam(customHyperparams, "size", native);
    if (next === null) return;
    onCustomHyperparamsChange(next);
    onHyperparamsErrorChange(null);
  };

  const handleFreezeModeChange = (mode: FreezeMode) => {
    const next = writeFreezeMode(customHyperparams, mode);
    if (next === null) return;
    onCustomHyperparamsChange(next);
    onHyperparamsErrorChange(null);
  };

  return (
    <>
      <SettingsCard
        stepNumber={6}
        state={customHyperparams.trim() ? "complete" : "pending"}
        title={t("advanced.title")}
        collapsible
        open={advancedOpen}
        onOpenChange={setAdvancedOpen}
      >
        <div className="flex flex-1 flex-col gap-4">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium leading-5 text-black/90">
              {t("advanced.modelSize.label")}
            </span>
            <p className="text-sm leading-5 text-black/60">
              {t("advanced.modelSize.description")}
            </p>
            <div className="flex flex-row gap-2 py-2">
              {SELECTABLE_MODEL_SIZES.map((size) => {
                const selected = modelSize === size;
                return (
                  <button
                    key={size}
                    type="button"
                    disabled={!paramsEditable}
                    onClick={() => handleModelSizeChange(size)}
                    className={cn(
                      "rounded-full border px-3 py-0.5 text-sm leading-5 transition-colors",
                      !paramsEditable
                        ? "cursor-not-allowed border-transparent bg-white text-black/30"
                        : selected
                          ? "cursor-pointer border-emerald-900 bg-emerald-50 text-emerald-700"
                          : "cursor-pointer border-transparent bg-white text-black/90 hover:bg-black/5",
                    )}
                  >
                    {t(MODEL_SIZE_LABELS[size])}
                  </button>
                );
              })}
            </div>
            {modelSize && (
              <span className="text-xs leading-4 text-black/50">
                {t(MODEL_SIZE_HINTS[modelSize])}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium leading-5 text-black/90">
              {t("advanced.region.label")}
            </span>
            <p className="text-sm leading-5 text-black/60">
              {t("advanced.region.description")}
            </p>
            <div className="flex flex-row gap-2 py-2">
              {Object.values(ModelRegionEnum).map((value) => {
                const selected = region === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onRegionChange(value)}
                    className={cn(
                      "cursor-pointer rounded-full border px-3 py-0.5 text-sm leading-5 transition-colors",
                      selected
                        ? "border-emerald-900 bg-emerald-50 text-emerald-700"
                        : "border-transparent bg-white text-black/90 hover:bg-black/5",
                    )}
                  >
                    {REGION_LABELS[value]}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium leading-5 text-black/90">
              {t("fields.outputFormats")}
            </span>
            <p className="text-sm leading-5 text-black/60">
              {t("advanced.outputFormats.description")}
            </p>
            <div className="flex flex-row gap-2 py-2">
              {Object.values(ModelOutputTypeEnum)
                // Segmentation runs on RVC4 only; offering the others would
                // just fail at conversion.
                .filter((outputType) =>
                  isConversionTargetSupported(backend, trainingType, outputType),
                )
                .map((outputType) => {
                const selected = outputs.includes(outputType);
                return (
                  <button
                    key={outputType}
                    type="button"
                    onClick={() =>
                      selected
                        ? onOutputsChange(
                            outputs.filter((t) => t !== outputType),
                          )
                        : onOutputsChange([...outputs, outputType])
                    }
                    className={cn(
                      "cursor-pointer rounded-full border px-2 py-0.5 text-sm leading-5 transition-colors",
                      selected
                        ? "border-emerald-900 bg-emerald-50 text-emerald-700"
                        : "border-transparent bg-white text-black/90 hover:bg-black/5",
                    )}
                  >
                    {outputType}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium leading-5 text-black/90">
              {t("advanced.precision.label")}
            </span>
            <p className="text-sm leading-5 text-black/60">
              {t("advanced.precision.description")}
            </p>
            <div className="flex flex-row gap-2 py-2">
              {Object.values(ModelQuantizationEnum).map((value) => {
                const selected = quantization === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onQuantizationChange(value)}
                    className={cn(
                      "cursor-pointer rounded-full border px-2 py-0.5 text-sm leading-5 transition-colors",
                      selected
                        ? "border-emerald-900 bg-emerald-50 text-emerald-700"
                        : "border-transparent bg-white text-black/90 hover:bg-black/5",
                    )}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium leading-5 text-black/90">
              {t("advanced.freeze.label")}
            </span>
            <p className="text-sm leading-5 text-black/60">
              {t("advanced.freeze.description")}
            </p>
            <div className="flex flex-row gap-2 py-2">
              {FREEZE_MODES.map((mode) => {
                const selected = freezeMode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    disabled={!paramsEditable}
                    onClick={() => handleFreezeModeChange(mode)}
                    className={cn(
                      "rounded-full border px-3 py-0.5 text-sm leading-5 transition-colors",
                      !paramsEditable
                        ? "cursor-not-allowed border-transparent bg-white text-black/30"
                        : selected
                          ? "cursor-pointer border-emerald-900 bg-emerald-50 text-emerald-700"
                          : "cursor-pointer border-transparent bg-white text-black/90 hover:bg-black/5",
                    )}
                  >
                    {t(FREEZE_MODE_LABELS[mode])}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-0.5">
            <div className="flex flex-row items-center gap-2">
              <span className="text-sm font-medium leading-5 text-black/90">
                {t("fields.customHyperparams")}
              </span>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="cursor-pointer text-sm font-medium leading-5 text-emerald-700 hover:underline"
              >
                {hasSummary ? t("common:actions.edit") : t("advanced.configure")}
              </button>
              {hasSummary && (
                <button
                  type="button"
                  onClick={() => {
                    onCustomHyperparamsChange("");
                    onHyperparamsErrorChange(null);
                  }}
                  className="cursor-pointer text-sm font-medium leading-5 text-black/60 hover:underline"
                >
                  {t("common:actions.clear")}
                </button>
              )}
            </div>
            <div className="flex flex-col gap-1 pt-1">
              <p className="text-sm leading-5 text-black/60">
                {t("advanced.customHyperparams.description")}
              </p>
              {hasSummary && (
                <pre className="mt-2 overflow-x-auto whitespace-pre rounded bg-black/[0.04] p-2 font-mono text-xs text-muted-foreground">
                  {customHyperparams}
                </pre>
              )}
            </div>
            {hyperparamsError && (
              <span className="text-xs text-red-500">{hyperparamsError}</span>
            )}
          </div>
        </div>
      </SettingsCard>

      {modalOpen && (
        <HyperparamsModal
          backend={backend}
          trainingType={trainingType}
          value={customHyperparams}
          onApply={handleApply}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};
